<template>
  <div class="profile-dropdown-wrapper">
    <VaDropdown v-model="isShown" :offset="[9, 0]" class="profile-dropdown" stick-to-edges>
      <template #anchor>
        <VaButton preset="secondary" color="textPrimary" class="w-full">
          <span class="profile-dropdown__anchor w-full">
            <slot>
              <UserAvatar :size="32" color="primary" />
            </slot>
          </span>
        </VaButton>
      </template>
      <VaDropdownContent
        class="profile-dropdown__content md:w-64 px-0 py-4 w-full"
        :style="{ '--hover-color': hoverColor }"
      >
        <div class="pt-2 px-4 pb-4">
          <p class="font-bold">{{ authStore.memberFullName }}</p>
          <p class="text-xs">{{ authStore.memberDetails?.emailAddress || 'No email provided' }}</p>
        </div>
        <VaList v-for="group in filteredOptions" :key="group.name">
          <header v-if="group.name" class="uppercase text-[var(--va-secondary)] opacity-80 font-bold text-xs px-4">
            {{ t(`user.${group.name}`) }}
          </header>
          <VaListItem
            v-for="item in group.list"
            :key="item.name"
            class="menu-item px-4 cursor-pointer h-8"
            v-bind="resolveLinkAttribute(item)"
            @click="handleItemClick(item)"
          >
            <VaIcon :name="item.icon" class="pr-1" color="secondary" />
            {{ t(`user.${item.name}`) }}
          </VaListItem>
          <VaListSeparator v-if="group.separator" class="mx-3 my-2" />
        </VaList>
      </VaDropdownContent>
    </VaDropdown>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'
// import { useRouter } from 'vue-router'
import { useAuthStore } from '../../../../stores/auth'
import { isApproved } from '../../../../utils/approvalUtils'
import UserAvatar from '../../../UserAvatar.vue'

const { colors, setHSLAColor } = useColors()
const hoverColor = computed(() => setHSLAColor(colors.focus, { a: 0.1 }))

const { t } = useI18n()
// const router = useRouter()
const authStore = useAuthStore()

type ProfileListItem = {
  name: string
  to?: string
  href?: string
  action?: string
  icon: string
}

type ProfileOptions = {
  name: string
  separator: boolean
  list: ProfileListItem[]
}

const props = withDefaults(
  defineProps<{
    options?: ProfileOptions[]
  }>(),
  {
    options: () => [
      {
        name: '',
        separator: true,
        list: [
          {
            name: 'profile',
            to: 'profile',
            icon: 'mso-account_circle',
          },
          {
            name: 'settings',
            to: 'settings',
            icon: 'mso-settings',
          },
        ],
      },
      {
        name: '',
        separator: true,
        list: [
          {
            name: 'helpAndSupport',
            href: 'mailto:members@holycross.ke',
            icon: 'mso-help',
          },
        ],
      },
      {
        name: '',
        separator: false,
        list: [
          {
            name: 'logout',
            action: 'logout',
            icon: 'mso-logout',
          },
        ],
      },
    ],
  },
)

const isShown = ref(false)

// Filter options based on approval status
const filteredOptions = computed(() => {
  const userApprovalStatus = authStore.memberDetails?.approvalStatus
  const approved = isApproved(userApprovalStatus)

  // If user is not approved, filter out profile and settings items
  if (!approved) {
    return props.options
      .map((group: ProfileOptions) => ({
        ...group,
        list: group.list.filter((item: ProfileListItem) => item.name !== 'profile' && item.name !== 'settings'),
      }))
      .filter((group: ProfileOptions) => group.list.length > 0) // Remove empty groups
  }

  return props.options
})

const handleItemClick = (item: ProfileListItem) => {
  if (item.action === 'logout') {
    authStore.logout()
    isShown.value = false
  }
}

const resolveLinkAttribute = (item: ProfileListItem) => {
  // Don't add navigation attributes for action items
  if (item.action) return {}
  return item.to ? { to: { name: item.to } } : item.href ? { href: item.href, target: '_blank' } : {}
}
</script>

<style lang="scss">
.profile-dropdown {
  cursor: pointer;

  .profile-dropdown__icon {
    position: relative;
    display: flex;
    align-items: center;
  }

  &__content {
    .menu-item:hover {
      background: var(--hover-color);
    }
  }

  &__anchor {
    display: inline-block;
  }
}
</style>
